import { Box } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import { useReportData } from "./UseReportData";
import { ChartCard, Empty, KpiBand, SectionFrame, SimpleTable, StatusKey, bucketLabel, bucketTitle, fmt, fmtPct, shortNum, statusOf, useChartColors, varPct } from "./Reportkit";

function HBars({ data, nameKey, valueKey, color, unit = "", height }) {
  const c = useChartColors();
  if (!data.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={height || Math.max(180, data.length * 34 + 40)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={c.grid} />
        <XAxis type="number" tick={c.tick} tickFormatter={shortNum} />
        <YAxis type="category" dataKey={nameKey} width={130} tick={c.tick} />
        <RTooltip formatter={(v) => `${fmt(v, 1)}${unit}`} />
        <Bar dataKey={valueKey} fill={color} barSize={16} radius={[0, 4, 4, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Body({ ev, sf }) {
  const c = useChartColors();
  const k = ev.kpis, p = ev.previousKpis;
  const prev = (v, pv) => ({ status: statusOf(v, pv, "lower"), delta: varPct(v, pv) });
  const compliance = sf.attendances ? (sf.flhaCompliant / sf.attendances) * 100 : null;
  const mttr = k.unplannedCount ? k.unplannedHours / k.unplannedCount : null;

  const downtime = ev.trend.map((x) => ({ label: bucketLabel(x.date, ev.grain), title: bucketTitle(x.date, ev.grain), Breakdown: x.breakdownHours, NPT: x.nptHours }));
  const flhaTrend = sf.trend.map((x) => ({ label: bucketLabel(x.bucket, sf.grain), compliance: x.attendances ? (x.flha / x.attendances) * 100 : null }));
  const pie = ev.plannedVsUnplanned.map((x) => ({ name: x.type, value: x.hours }));
  const pieColors = { Planned: "#2A78D6", Unplanned: "#E34948" };

  return (
    <>
      <KpiBand items={[
        { label: "Breakdowns", value: fmt(k.breakdownCount), sub: `${fmt(k.unplannedCount)} unplanned · prev ${fmt(p.breakdownCount)}`, ...prev(k.breakdownCount, p.breakdownCount) },
        { label: "Unplanned downtime", value: `${fmt(k.unplannedHours, 0)} h`, sub: `Prev ${fmt(p.unplannedHours, 0)} h`, ...prev(k.unplannedHours, p.unplannedHours) },
        { label: "Mean repair time", value: mttr == null ? "—" : `${fmt(mttr, 1)} h`, sub: "unplanned, closed events" },
        { label: "NPT logged", value: `${fmt(k.nptHours, 0)} h`, sub: `${fmt(k.nptCount)} reports · prev ${fmt(p.nptHours, 0)} h`, ...prev(k.nptHours, p.nptHours) },
        { label: "FLHA compliance", value: fmtPct(compliance, 1), sub: `${fmt(sf.flhaCompliant)} of ${fmt(sf.attendances)} shift attendances` },
        { label: "Checklist defects", value: fmt(sf.defects), sub: `${fmt(sf.checklists)} checklists` },
      ]} />
      <ChartCard title="Downtime and non-productive time" sub={`Hours by ${ev.grain} — closed breakdowns and operator-logged NPT`}>
        {downtime.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={downtime} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
              <XAxis dataKey="label" tick={c.tick} minTickGap={14} /><YAxis tick={c.tick} width={48} tickFormatter={shortNum} />
              <RTooltip formatter={(v) => `${fmt(v, 1)} h`} labelFormatter={(l, pl) => pl?.[0]?.payload?.title || l} /><Legend />
              <Bar dataKey="Breakdown" stackId="a" fill="#E34948" maxBarSize={44} isAnimationActive={false} />
              <Bar dataKey="NPT" stackId="a" fill="#EDA100" maxBarSize={44} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        <ChartCard title="Breakdown hours by reason" sub="Top categories"><HBars data={ev.breakdownReasons} nameKey="reason" valueKey="hours" color="#E34948" unit=" h" /></ChartCard>
        <ChartCard title="NPT hours by reason" sub="Operator-logged"><HBars data={ev.nptReasons} nameKey="reason" valueKey="hours" color="#EDA100" unit=" h" /></ChartCard>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" }, gap: 2 }}>
        <ChartCard title="Planned vs unplanned" sub="Breakdown hours">
          {pie.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart><Pie data={pie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2} isAnimationActive={false}>
                {pie.map((x) => <Cell key={x.name} fill={pieColors[x.name] || "#9CA3AF"} />)}</Pie>
                <RTooltip formatter={(v) => `${fmt(v, 1)} h`} /><Legend /></PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Day vs night" sub="Breakdown and NPT hours">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={ev.byShift} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} /><XAxis dataKey="shift" tick={c.tick} /><YAxis tick={c.tick} width={44} />
              <RTooltip formatter={(v) => `${fmt(v, 1)} h`} /><Legend />
              <Bar dataKey="breakdownHours" name="Breakdown" fill="#E34948" isAnimationActive={false} /><Bar dataKey="nptHours" name="NPT" fill="#EDA100" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="By equipment model" sub="Hours"><HBars data={ev.byModel} nameKey="model" valueKey="breakdownHours" color="#6250D6" unit=" h" height={220} /></ChartCard>
      </Box>
      <ChartCard title="Units with most downtime" sub="Closed breakdown hours and NPT hours (NPT unit comes from the shift's equipment assignment)">
        <SimpleTable max={360} cols={[
          { key: "unit", label: "Unit", align: "left", render: (r) => <b>{r.unit}</b> }, { key: "model", label: "Model", align: "left" },
          { key: "breakdownCount", label: "Breakdowns", render: (r) => <>{fmt(r.breakdownCount)}{r.breakdownCount >= 3 && <b style={{ color: "#D03B3B" }}> · repeat</b>}</> },
          { key: "breakdownHours", label: "BD hours", render: (r) => fmt(r.breakdownHours, 1) }, { key: "nptHours", label: "NPT hours", render: (r) => fmt(r.nptHours, 1) },
        ]} rows={ev.topUnits} />
      </ChartCard>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        <ChartCard title="FLHA compliance" sub={`Share of shift attendances with an FLHA, by ${sf.grain}`}>
          {flhaTrend.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={flhaTrend} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} /><XAxis dataKey="label" tick={c.tick} minTickGap={14} />
                <YAxis tick={c.tick} width={44} domain={[0, 100]} tickFormatter={(v) => `${v}%`} /><RTooltip formatter={(v) => fmtPct(v, 1)} />
                <Line type="monotone" dataKey="compliance" stroke={c.brand} strokeWidth={2.5} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Pre-start checklist defects" sub="Items flagged not OK"><HBars data={sf.defectsByItem} nameKey="name" valueKey="count" color="#EB6834" height={230} /></ChartCard>
      </Box>
      <ChartCard title="Most common FLHA tasks" sub="Free text, trimmed"><HBars data={sf.topTasks} nameKey="name" valueKey="count" color="#2A78D6" /></ChartCard>
      <StatusKey basis="prev" />
      <span style={{ fontSize: 12, opacity: 0.7 }}>No plan exists for events, so status compares with the previous period of equal length (lower is better). Open breakdowns have no end time and are excluded from hours. Breakdown shift is derived from start time (America/Edmonton). The checklist "not OK" flag may be inverted in the app (DQ-14) — treat defect rates with care.</span>
    </>
  );
}

export default function SafetyEventsSection({ req }) {
  const ev = useReportData("events", req);
  const sf = useReportData("safety", req);
  const state = { data: ev.data && sf.data ? true : null, loading: ev.loading || sf.loading, error: ev.error || sf.error, reload: () => { ev.reload(); sf.reload(); } };
  return (
    <SectionFrame eyebrow="Hazards · NPT · breakdowns · checklists" title="Safety & Events" desc="Frequency, type and timing of every field event" state={state}>
      {ev.data && sf.data && <Body ev={ev.data} sf={sf.data} />}
    </SectionFrame>
  );
}